<template>
  <div class="stock-invoice-payments">
    <div class="subtitle-2 mb-2">{{ historyTitle }}</div>
    <v-row>
      <v-col cols="12" md="3">
        <v-text-field
          :value="formatMoney(paidAmount)"
          :label="paidLabel"
          disabled
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          :value="formatMoney(remainingAmount)"
          :label="remainingLabel"
          disabled
          dense
        ></v-text-field>
      </v-col>
      <template v-if="canRecord">
        <v-col cols="12" md="2">
          <v-text-field
            v-model.number="form.amount"
            type="number"
            step="0.01"
            min="0.01"
            :max="maxPaymentAmount"
            :label="amountLabel"
            :disabled="maxPaymentAmount <= 0"
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <DatePicker
            v-model="form.paid_at"
            :label="$t('STOCK.INVOICES.PAYMENT_DATE')"
            without-hint
            without-icon
          ></DatePicker>
        </v-col>
        <v-col cols="12" md="2" class="d-flex align-center">
          <v-btn color="success" small :disabled="!canSubmit" @click="submit">
            {{ recordLabel }}
          </v-btn>
        </v-col>
      </template>
    </v-row>

    <v-simple-table v-if="payments.length" dense class="stock-invoice-payments__table mt-2">
      <thead>
        <tr>
          <th>{{ $t("STOCK.INVOICES.PAYMENT_DATE") }}</th>
          <th class="text-right">{{ amountLabel }}</th>
          <th>{{ $t("STOCK.INVOICES.RECORDED_BY") }}</th>
          <th>{{ $t("STOCK.INVOICES.PAYMENT_STATUS") }}</th>
          <th v-if="canRecord" class="text-right"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="payment in payments"
          :key="payment.id"
          :class="{ 'stock-invoice-payments__row--cancelled': payment.is_cancelled }"
        >
          <td>{{ formatDate(payment.paid_at) }}</td>
          <td class="text-right font-weight-medium stock-invoice-payments__amount">{{ formatMoney(payment.amount) }}</td>
          <td>{{ payment.created_by_user ? payment.created_by_user.name : "—" }}</td>
          <td>
            <span v-if="payment.is_cancelled">
              {{ $t("STOCK.INVOICES.PAYMENT_CANCELLED") }}
              <span v-if="payment.cancelled_by_user" class="caption grey--text">
                ({{ payment.cancelled_by_user.name }})
              </span>
            </span>
            <span v-else>{{ $t("STOCK.INVOICES.PAYMENT_RECORDED") }}</span>
          </td>
          <td v-if="canRecord" class="text-right">
            <v-btn
              v-if="!payment.is_cancelled"
              color="error"
              small
              text
              @click="$emit('cancel', payment)"
            >
              {{ $t("STOCK.INVOICES.ANNUL_PAYMENT") }}
            </v-btn>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <p v-else class="caption grey--text mb-0">{{ emptyLabel }}</p>
  </div>
</template>

<script>
import DatePicker from "@/views/partials/form/DatePicker.vue";

export default {
  name: "StockInvoicePaymentsPanel",
  components: { DatePicker },
  props: {
    payments: { type: Array, default: () => [] },
    paidAmount: { type: [Number, String], default: 0 },
    remainingAmount: { type: [Number, String], default: 0 },
    currencyCode: { type: String, default: "EUR" },
    canRecord: { type: Boolean, default: false },
    settlement: { type: String, default: "payment" },
    direction: { type: String, default: "outgoing" },
  },
  data() {
    return {
      form: {
        amount: null,
        paid_at: new Date().toISOString().slice(0, 10),
      },
    };
  },
  computed: {
    isRefund() {
      return this.settlement === "refund";
    },
    maxPaymentAmount() {
      return Math.max(0, Number(this.remainingAmount || 0));
    },
    historyTitle() {
      return this.$t(this.isRefund ? "STOCK.INVOICES.REFUND_HISTORY" : "STOCK.INVOICES.PAYMENT_HISTORY");
    },
    paidLabel() {
      return this.$t(this.isRefund ? "STOCK.INVOICES.REFUNDED_AMOUNT" : "STOCK.INVOICES.PAID_AMOUNT");
    },
    remainingLabel() {
      return this.$t(this.isRefund ? "STOCK.INVOICES.REFUND_REMAINING" : "STOCK.INVOICES.REMAINING_AMOUNT");
    },
    amountLabel() {
      return this.$t(this.isRefund ? "STOCK.INVOICES.REFUND_AMOUNT" : "STOCK.INVOICES.PAYMENT_AMOUNT");
    },
    recordLabel() {
      if (!this.isRefund) {
        return this.$t("STOCK.INVOICES.RECORD_PAYMENT");
      }
      return this.$t(
        this.direction === "incoming"
          ? "STOCK.INVOICES.RECORD_REFUND_RECEIVED"
          : "STOCK.INVOICES.RECORD_REFUND"
      );
    },
    emptyLabel() {
      return this.$t(this.isRefund ? "STOCK.INVOICES.NO_REFUNDS" : "STOCK.INVOICES.NO_PAYMENTS");
    },
    canSubmit() {
      return this.maxPaymentAmount > 0 && Number(this.form.amount) > 0;
    },
  },
  methods: {
    formatMoney(value) {
      return `${Number(value || 0).toFixed(2)} ${this.currencyCode || "EUR"}`.trim();
    },
    formatDate(value) {
      if (!value) {
        return "—";
      }
      const [year, month, day] = String(value).slice(0, 10).split("-");
      if (!year || !month || !day) {
        return value;
      }
      return `${day}.${month}.${year}`;
    },
    submit() {
      const remaining = this.maxPaymentAmount;
      let amount = Number(this.form.amount);
      if (!amount || amount <= 0 || remaining <= 0) {
        return;
      }
      if (amount > remaining) {
        amount = remaining;
      }
      this.$emit("record", {
        amount: Math.round(amount * 10000) / 10000,
        paid_at: this.form.paid_at || new Date().toISOString().slice(0, 10),
      });
    },
    resetForm() {
      this.form.amount = null;
      this.form.paid_at = new Date().toISOString().slice(0, 10);
    },
  },
};
</script>

<style scoped>
.stock-invoice-payments__row--cancelled td {
  color: #9e9e9e;
}
.stock-invoice-payments__row--cancelled .stock-invoice-payments__amount {
  text-decoration: line-through;
}
</style>
